export type CacheEntry<T> = {
  v: T;
  exp: number;
};

const NS = 'profileCache';

const now = () => Date.now();

const toKey = (userId: string, field: 'alias' | 'avatar' | 'email') => `${NS}:${userId}:${field}`;

const ALIAS_TTL_MS = 24 * 60 * 60 * 1000;
const AVATAR_TTL_MS = 60 * 60 * 1000;
const EMAIL_TTL_MS = 24 * 60 * 60 * 1000;

function getItem<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.exp !== 'number') return null;
    if (parsed.exp < now()) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.v as T;
  } catch {
    return null;
  }
}

function setItem<T>(key: string, value: T, ttlMs: number) {
  try {
    const entry: CacheEntry<T> = { v: value, exp: now() + ttlMs };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
  }
}

export function getCachedAlias(userId: string): string | null {
  return getItem<string>(toKey(userId, 'alias'));
}

export function cacheAlias(userId: string, alias: string | null) {
  if (!userId || !alias) return;
  setItem<string>(toKey(userId, 'alias'), alias, ALIAS_TTL_MS);
}

export function getCachedAvatar(userId: string): string | null {
  return getItem<string>(toKey(userId, 'avatar'));
}

export function cacheAvatar(userId: string, url: string | null, customTtlMs?: number) {
  if (!userId || !url) return;
  setItem<string>(toKey(userId, 'avatar'), url, typeof customTtlMs === 'number' ? customTtlMs : AVATAR_TTL_MS);
}

export function getCachedEmail(userId: string): string | null {
  return getItem<string>(toKey(userId, 'email'));
}

export function cacheEmail(userId: string, email: string | null) {
  if (!userId || !email) return;
  setItem<string>(toKey(userId, 'email'), email, EMAIL_TTL_MS);
}

export function invalidateUserProfileCache(userId: string) {
  try {
    localStorage.removeItem(toKey(userId, 'alias'));
    localStorage.removeItem(toKey(userId, 'avatar'));
    localStorage.removeItem(toKey(userId, 'email'));
  } catch {}
}

export function invalidateAvatar(userId: string) {
  try { localStorage.removeItem(toKey(userId, 'avatar')); } catch {}
}

export function invalidateAlias(userId: string) {
  try { localStorage.removeItem(toKey(userId, 'alias')); } catch {}
}
